import { QueryClient } from '@tanstack/react-query';

import { isClientError } from './is-client-error';

export const retryAllFailedMutations = (queryClient: QueryClient) => {
  const failedMutations = queryClient.getMutationCache().findAll({
    status: 'error',
    mutationKey: [],
    predicate(mutation) {
      // Only retry non client errors
      return !isClientError(mutation.state.error);
    },
  });

  return failedMutations.map((mutation) => {
    return retryFailedMutation(queryClient, mutation.mutationId);
  });
};

export const retryFailedMutation = (
  queryClient: QueryClient,
  mutationId: number
) => {
  const mutation = queryClient.getMutationCache().find({
    predicate(mutation) {
      return mutation.mutationId === mutationId;
    },
  });
  if (!mutation) {
    return;
  }

  mutation.state.failureCount = 0;
  mutation.state.error = null;
  mutation.state.status = 'pending';
  // Notify the mutation cache that the mutation is being retried (this is important for the state to be updated)
  // we change the action to "continue" to avoid the mutation from being altered
  // https://github.com/TanStack/query/blob/main/packages/query-core/src/mutation.ts#L289
  queryClient.getMutationCache().notify({
    mutation,
    type: 'updated',
    action: {
      type: 'continue',
    },
  });

  // Retry the mutation with the original variables
  return mutation.execute(mutation.state.variables);
};

export const clearAllFailedMutations = (queryClient: QueryClient) => {
  const failedMutations = queryClient.getMutationCache().findAll({
    status: 'error',
    mutationKey: [],
  });

  return failedMutations.map((mutation) => {
    return clearMutation(queryClient, mutation.mutationId);
  });
};

export const clearAllMutations = (queryClient: QueryClient) => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: [],
    predicate(mutation) {
      return mutation.state.status !== 'pending';
    },
  });

  return mutations.map((mutation) => {
    return clearMutation(queryClient, mutation.mutationId);
  });
};

export const clearFailedMutationsByKey = (
  queryClient: QueryClient,
  mutationKey: readonly unknown[]
) => {
  queryClient
    .getMutationCache()
    .findAll({ mutationKey, status: 'error' })
    .forEach((mutation) => {
      queryClient.getMutationCache().remove(mutation);
    });
};

export const clearSucceededMutationsByKey = (
  queryClient: QueryClient,
  mutationKey: readonly unknown[]
) => {
  queryClient
    .getMutationCache()
    .findAll({ mutationKey, status: 'success' })
    .forEach((mutation) => {
      queryClient.getMutationCache().remove(mutation);
    });
};

export const clearMutationsByKey = (
  queryClient: QueryClient,
  mutationKey: readonly unknown[]
) => {
  queryClient
    .getMutationCache()
    .findAll({ mutationKey })
    .forEach((mutation) => {
      queryClient.getMutationCache().remove(mutation);
    });
};

/** Clears finished mutations (error/success) but keeps paused offline queue entries. */
export const clearCompletedMutationsByKey = (
  queryClient: QueryClient,
  mutationKey: readonly unknown[]
) => {
  queryClient
    .getMutationCache()
    .findAll({ mutationKey })
    .forEach((mutation) => {
      if (mutation.state.isPaused || mutation.state.status === 'pending') {
        return;
      }
      queryClient.getMutationCache().remove(mutation);
    });
};

/**
 * TanStack Query dispatches the final success/error status AFTER onSettled.
 * Defer removal so cleared mutations are not immediately re-added to the cache.
 */
export const scheduleClearCompletedMutationsByKey = (
  queryClient: QueryClient,
  mutationKey: readonly unknown[]
) => {
  queueMicrotask(() => {
    clearCompletedMutationsByKey(queryClient, mutationKey);
  });
};

/** Drop false-positive errors after the final error dispatch; keep success rows. */
export const scheduleClearFailedMutationsByKey = (
  queryClient: QueryClient,
  mutationKey: readonly unknown[]
) => {
  // Run after TanStack Query's synchronous error/success dispatch in mutation.execute().
  setTimeout(() => {
    clearFailedMutationsByKey(queryClient, mutationKey);
  }, 0);
};

export const clearMutation = (queryClient: QueryClient, mutationId: number) => {
  const mutation = queryClient.getMutationCache().find({
    predicate(mutation) {
      return mutation.mutationId === mutationId;
    },
  });
  if (!mutation) {
    return;
  }
  queryClient.getMutationCache().remove(mutation);
};
